import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
// import { MENU_BOOK_LIST } from './mockdata';
import { VideoFunctionListItem } from './models';
import { MultiSelectToArg } from './utils/dragutils/types';
import { multiSelectTo } from './utils/dragutils/utils';

export interface VideoFunctionState {
  loading: boolean;
  data: VideoFunctionListItem[];
  selectedItemIds: string[];
  selectedDroppableId?: string;
  draggingItemId?: string;
}

const initialState: VideoFunctionState = {
  loading: false,
  data: [],
  selectedItemIds: [],
};

const videoFunctionSlice = createSlice({
  name: 'video_function',
  initialState,
  reducers: {
    setVideoFunctionList(state, action: PayloadAction<VideoFunctionListItem[]>) {
      state.data = action.payload;
    },
    unselectAll(state) {
      state.selectedItemIds = [];
      state.draggingItemId = undefined;
      state.selectedDroppableId = undefined;
    },
    setDraggingItemId(state, action: PayloadAction<string | undefined>) {
      state.draggingItemId = action.payload;
    },
    updateState(state, action: PayloadAction<Partial<VideoFunctionState>>) {
      state.data = action.payload.data || [];
      state.selectedItemIds = action.payload.selectedItemIds || [];
      state.draggingItemId = action.payload.draggingItemId;
    },
    toggleSelection(state, action: PayloadAction<{ selectedDroppableId: string; itemId: string }>) {
      const selectedItemIds = state.selectedItemIds;
      const wasSelected: boolean =
        state.selectedDroppableId === action.payload.selectedDroppableId &&
        selectedItemIds.includes(action.payload.itemId);

      const newItemIds: string[] = (() => {
        // Task was not previously selected
        // now will be the only selected item
        if (!wasSelected) {
          return [action.payload.itemId];
        }

        // Task was part of a selected group
        // will now become the only selected item
        if (selectedItemIds.length > 1) {
          return [action.payload.itemId];
        }

        // task was previously selected but not in a group
        // we will now clear the selection
        return [];
      })();

      state.selectedDroppableId = action.payload.selectedDroppableId;
      state.selectedItemIds = newItemIds;
    },

    toggleSelectionInGroup(state, action: PayloadAction<{ selectedDroppableId: string; itemId: string }>) {
      const selectedItemIds = state.selectedItemIds;
      const index: number = selectedItemIds.indexOf(action.payload.itemId);

      // if not selected - add it to the selected items
      if (index === -1) {
        state.selectedItemIds = [...selectedItemIds, action.payload.itemId];
        return;
      }

      // it was previously selected and now needs to be removed from the group
      const shallow = [...selectedItemIds];
      shallow.splice(index, 1);
      state.selectedItemIds = shallow;

      state.selectedDroppableId = action.payload.selectedDroppableId;
    },

    multiSelectTo(state, action: PayloadAction<{ arg: MultiSelectToArg; newItemId: string }>) {
      const updated = multiSelectTo(action.payload.arg, action.payload.newItemId);

      if (updated == null) {
        return;
      }
      state.selectedItemIds = updated;
    },
  },
});

// Actions
export const videoFunctionActions = videoFunctionSlice.actions;

// Selectors
export const selectVideoFunctionLoading = (state: RootState) => state.videoFunction.loading;
export const selectVideoFunctionData = (state: RootState) => state.videoFunction.data;
export const selectVideoFunctionSelectedDroppableId = (state: RootState) => state.videoFunction.selectedDroppableId;
export const selectVideoFunctionSelectedItemIds = (state: RootState) => state.videoFunction.selectedItemIds;
export const selectVideoFunctionDraggingItemId = (state: RootState) => state.videoFunction.draggingItemId;

export const selectVideoFunctionSelectedItemIdMap = createSelector(
  selectVideoFunctionSelectedItemIds,
  (selectedItemIds) => {
    return selectedItemIds.reduce((previous: { [id: string]: true }, current: string): { [id: string]: true } => {
      previous[current] = true;
      return previous;
    }, {});
  }
);

// Reducer
export const videoFunctionReducer = videoFunctionSlice.reducer;
